import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=State%20of%20the%20art%20IVF%20embryology%20laboratory%20with%20scientists%20in%20white%20coats%20working%20with%20microscopes%2C%20clean%20modern%20medical%20research%20facility%2C%20warm%20white%20and%20champagne%20gold%20tones%2C%20professional%20precision%20fertility%20science%20environment%2C%20soft%20natural%20lighting&width=1600&height=700&seq=ivf3-hero-2026&orientation=landscape';

const STEP_IMGS = [
  'https://readdy.ai/api/search-image?query=Fertility%20doctor%20couple%20consultation%20IVF%20plan%2C%20professional%20medical%20office%20warm%20champagne%20light%2C%20doctor%20explaining%20test%20results%20charts%20to%20couple%2C%20modern%20clean%20interior%2C%20soft%20tones&width=600&height=400&seq=ivf3-step-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Ovarian%20stimulation%20injection%20fertility%20treatment%2C%20nurse%20administering%20hormone%20injection%20to%20patient%2C%20warm%20clinic%20room%2C%20champagne%20tones%2C%20professional%20gentle%20care%2C%20modern%20medical%20equipment&width=600&height=400&seq=ivf3-step-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Egg%20retrieval%20procedure%20operating%20room%2C%20medical%20team%20fertility%20clinic%2C%20professional%20surgical%20team%2C%20sterile%20medical%20environment%20clean%20white%20tones%2C%20warm%20ambient%20lighting&width=600&height=400&seq=ivf3-step-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Sperm%20laboratory%20preparation%20fertilization%20science%2C%20clean%20lab%20microscope%20sperm%20sample%20processing%2C%20professional%20embryologist%20white%20coat%2C%20warm%20white%20and%20champagne%20tones%2C%20scientific%20precision&width=600&height=400&seq=ivf3-step-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Embryo%20culture%20laboratory%20incubator%2C%20embryologist%20examining%20growing%20embryos%20under%20microscope%2C%20clean%20modern%20lab%2C%20warm%20white%20clinical%20tones%2C%20professional%20precision%20science%2C%20soft%20lighting&width=600&height=400&seq=ivf3-step-05&orientation=landscape',
  'https://readdy.ai/api/search-image?query=PGT%20genetic%20testing%20embryo%20biopsy%2C%20embryologist%20with%20fine%20needle%20extracting%20single%20cell%20from%20embryo%2C%20high%20magnification%20microscope%2C%20clean%20lab%20warm%20tones%2C%20professional%20precision&width=600&height=400&seq=ivf3-step-06&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Embryo%20transfer%20procedure%20fertility%20clinic%2C%20doctor%20performing%20embryo%20transfer%20with%20catheter%20ultrasound%20monitor%2C%20professional%20sterile%20medical%20room%2C%20warm%20white%20tones%2C%20hopeful%20atmosphere&width=600&height=400&seq=ivf3-step-07&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Pregnancy%20test%20positive%20result%20blood%20test%2C%20happy%20couple%20holding%20ultrasound%20scan%2C%20warm%20champagne%20tones%20clinic%2C%20emotional%20positive%20pregnancy%20confirmation%2C%20soft%20natural%20lighting&width=600&height=400&seq=ivf3-step-08&orientation=landscape',
];

const STEP_ICONS = ['ri-stethoscope-line', 'ri-syringe-line', 'ri-hospital-line', 'ri-microscope-line', 'ri-flask-line', 'ri-dna-line', 'ri-heart-pulse-line', 'ri-emotion-happy-line'];
const ITEM_ICONS = ['ri-stethoscope-line', 'ri-microscope-line', 'ri-test-tube-line', 'ri-syringe-line', 'ri-dna-line', 'ri-flask-line', 'ri-search-line', 'ri-shield-check-line', 'ri-refresh-line', 'ri-snowflake-line', 'ri-car-line', 'ri-hotel-line'];

const ivfJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: '三代试管（PGT-A）- RTB吉尔吉斯斯坦试管代孕中心',
    description: '单周期三代试管套餐$19,000，含PGT-A基因筛查、ICSI单精子注射、全部胚胎培养及活检，8步专业流程。',
    url: `${SITE_URL}/ivf`,
  },
];

export default function IVFDetailPage() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = useMemo(() => Array.from({ length: 8 }, (_, i) => {
    const n = i + 1;
    return {
      num: `0${n}`,
      title: t(`ivf_step${n}_title`),
      duration: t(`ivf_step${n}_dur`),
      desc: t(`ivf_step${n}_desc`),
      points: [t(`ivf_step${n}_p1`), t(`ivf_step${n}_p2`), t(`ivf_step${n}_p3`), t(`ivf_step${n}_p4`)],
    };
  }), [t]);

  const includedItems = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const n = i + 1;
    return {
      icon: ITEM_ICONS[i],
      title: t(`ivf_item${n}_title`),
      desc: t(`ivf_item${n}_desc`),
    };
  }), [t]);

  const extraItems = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const n = i + 1;
    return {
      title: t(`ivf_extra${n}_title`),
      price: t(`ivf_extra${n}_price`),
      desc: t(`ivf_extra${n}_desc`),
    };
  }), [t]);

  return (
    <>
      <SeoHead
        title="三代试管PGT-A｜单周期$19,000 - RTB吉尔吉斯斯坦试管代孕中心"
        description="RTB吉尔吉斯斯坦三代试管：单周期$19,000含PGT-A基因筛查、ICSI单精子注射、全部胚胎培养及活检，8步专业流程，远程+本地专家见诊。"
        keywords="三代试管,PGT-A基因筛查,ICSI单精子注射,吉尔吉斯斯坦试管婴儿,IVF费用"
        canonical="/ivf"
        jsonLd={ivfJsonLd}
      />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="relative h-[420px] md:h-[580px] flex items-center overflow-hidden">
          <img src={HERO_IMG} alt="三代试管" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2137]/85 via-[#0d2137]/55 to-transparent"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-4">
                <Link to="/services" className="text-[#c9a96e] text-xs hover:underline cursor-pointer">{t('ivf_breadcrumb_svc')}</Link>
                <i className="ri-arrow-right-s-line text-[#c9a96e] text-xs"></i>
                <span className="text-white/70 text-xs">{t('ivf_breadcrumb')}</span>
              </div>
              <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-3">{t('ivf_label')}</div>
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">{t('ivf_page_title')}</h1>
              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-8">
                {t('ivf_hero_desc')}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm">
                  <i className="ri-calendar-line"></i> {t('ivf_cta_consult')}
                </Link>
                <a href="https://wa.me/8618513885468" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm backdrop-blur-sm">
                  <i className="ri-whatsapp-line"></i> {t('ivf_cta_whatsapp')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key stats */}
        <section className="bg-[#1a3a5c] py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { num: '$19,000', labelKey: 'ivf_stat_price' },
                { num: '8颗', labelKey: 'ivf_stat_screen' },
                { num: '60-70%', labelKey: 'ivf_stat_rate' },
                { num: '1年', labelKey: 'ivf_stat_freeze' },
              ].map((s) => (
                <div key={s.labelKey}>
                  <div className="text-2xl md:text-3xl font-bold text-[#c9a96e] mb-1">{s.num}</div>
                  <div className="text-white/70 text-xs">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IVF流程 */}
        <section className="py-16 md:py-24 bg-[#f8f5f0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="text-[#c9a96e] text-xs font-medium tracking-widest uppercase mb-2">{t('ivf_process_label')}</div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('ivf_process_title')}</h2>
              <p className="text-gray-500 text-sm">{t('ivf_process_sub')}</p>
            </div>

            <div className="mb-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {steps.map((s, i) => (
                  <button key={i} onClick={() => setActiveStep(i)} className={`text-left p-4 rounded-xl transition-all cursor-pointer ${activeStep === i ? 'bg-[#1a3a5c] text-white' : 'bg-white hover:bg-[#f0e8d8]'}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full mb-3 text-sm font-bold ${activeStep === i ? 'bg-[#c9a96e] text-white' : 'bg-[#f8f5f0] text-[#c9a96e]'}`}>{s.num}</div>
                    <div className={`text-xs font-semibold mb-1 ${activeStep === i ? 'text-white' : 'text-[#1a3a5c]'}`}>{s.title}</div>
                    <div className={`text-xs ${activeStep === i ? 'text-white/60' : 'text-gray-400'}`}>{s.duration}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="h-64 md:h-80 lg:h-auto overflow-hidden">
                  <img src={STEP_IMGS[activeStep]} alt={steps[activeStep].title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-bold text-[#c9a96e]/30">{steps[activeStep].num}</span>
                    <div>
                      <div className="text-xs text-[#c9a96e] font-medium mb-0.5">{steps[activeStep].duration}</div>
                      <h3 className="text-xl md:text-2xl font-bold text-[#1a3a5c]">{steps[activeStep].title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{steps[activeStep].desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {steps[activeStep].points.map((p) => (
                      <div key={p} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#c9a96e] shrink-0 text-base"></i>{p}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-8">
                    <button onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))} disabled={activeStep === 0} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:border-[#1a3a5c] hover:text-[#1a3a5c] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button onClick={() => setActiveStep((prev) => Math.min(steps.length - 1, prev + 1))} disabled={activeStep === steps.length - 1} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1a3a5c] text-white hover:bg-[#2a5a8c] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                    <span className="ml-auto text-xs text-gray-400 self-center">{activeStep + 1} / {steps.length}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Included items */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('ivf_included_title')}</h2>
              <p className="text-gray-500 text-sm">{t('ivf_included_sub')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {includedItems.map((item) => (
                <div key={item.title} className="p-6 rounded-2xl bg-[#f8f5f0] hover:bg-[#f0e8d8] transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1a3a5c]/8 text-[#c9a96e] mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1a3a5c] text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Extra fees */}
        <section className="py-16 bg-[#f8f5f0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-3">{t('ivf_extra_title')}</h2>
              <p className="text-gray-500 text-sm">{t('ivf_extra_sub')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {extraItems.map((item) => (
                <div key={item.title} className="bg-white rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#f8f5f0] text-[#c9a96e] shrink-0">
                    <i className="ri-price-tag-3-line text-lg"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-[#1a3a5c] text-sm">{item.title}</h3>
                      <span className="text-xs text-[#c9a96e] font-semibold">{item.price}</span>
                    </div>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#1a3a5c]">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('ivf_cta_title')}</h2>
            <p className="text-white/70 text-sm mb-8">{t('ivf_cta_desc')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-calendar-check-line"></i> {t('ivf_cta_consult')}
              </Link>
              <a href="https://wa.me/8618513885468" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-whatsapp-line"></i> {t('ivf_cta_whatsapp')}
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}